import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Check, Phone, Calendar, Clock, MessageSquare, TrendingUp, Zap, ArrowRight, BarChart3, Shield, Sparkles, CheckCircle, Users, Bell, Star, LayoutGrid, Database, Lock, Command, Globe, Sliders, MonitorPlay, FileText, Activity, Settings, Layers, DollarSign, HeartPulse, BrainCircuit, ArrowUpRight, Smartphone } from 'lucide-react';

const KimPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        @keyframes pulse-ring {
          0% { transform: scale(0.8); opacity: 0.5; }
          100% { transform: scale(1.3); opacity: 0; }
        }
        .animate-pulse-ring {
          animation: pulse-ring 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        .glass-card {
          background: rgba(255, 255, 255, 0.7);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.5);
          box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.07);
        }
      `}</style>
      
      {/* Hero Section */}
      <section className="pt-24 pb-20 px-6 relative overflow-hidden">
        {/* Gradient Background - Blue dominant with subtle purple */}
        <div className="absolute inset-0 bg-gradient-to-b from-blue-50 via-indigo-50/30 to-white"></div>
        
        <div className="container mx-auto max-w-7xl relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left: Content */}
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/80 backdrop-blur-sm text-blue-700 text-xs font-medium border border-blue-200/50">
                <div className="w-1.5 h-1.5 bg-blue-600 rounded-full"></div>
                <span>Meet Kim, Case Acceptance Specialist</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-[56px] font-bold leading-tight tracking-tight">
                <span className="text-gray-900">Turn Unscheduled Treatment into</span><br />
                <span className="text-blue-600">Realized Revenue</span>
              </h1>
              
              <p className="text-lg text-gray-700 leading-relaxed max-w-lg">
                Kim doesn't just send reminders. She works from the treatment data migrated into Asha PMS to understand <strong>exactly</strong> what a patient needs, creates a clinical narrative to educate them on <strong>why</strong> it matters, and uses intelligent multi-channel campaigns to get them booked.
              </p>
              
              <div className="flex flex-wrap gap-3 pt-2">
                <Link to="/book-demo">
                  <button className="px-6 py-3 rounded-xl font-bold text-sm transition-all bg-blue-600 text-white hover:bg-blue-700 shadow-lg hover:shadow-blue-200">
                    Get a demo
                  </button>
                </Link>
                <button className="px-6 py-3 rounded-xl font-bold text-sm transition-all bg-white text-gray-700 hover:bg-gray-50 border border-gray-200 hover:border-gray-300">
                  See pricing
                </button>
              </div>
            </div>
            
            {/* Right: Unique Visual - Connecting Treatment to Patient */}
            <div className="relative flex items-center justify-center h-[500px]">
              <div className="relative w-full max-w-md">
                {/* Central Agent Hub */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20">
                  <div className="relative">
                    <div className="w-24 h-24 rounded-full bg-gradient-to-br from-blue-100 to-indigo-100 p-1 shadow-xl ring-4 ring-white">
                      <img src="/assets/kim.png" alt="Kim" className="w-full h-full rounded-full object-cover" />
                    </div>
                    {/* Pulsing Rings */}
                    <div className="absolute inset-0 rounded-full border-2 border-blue-400 opacity-20 animate-ping"></div>
                    <div className="absolute -inset-4 rounded-full border border-blue-200 opacity-40 animate-pulse"></div>
                  </div>
                </div>

                {/* Left Side: PMS Data (Treatment Plan) */}
                <div className="absolute top-20 left-0 bg-white p-4 rounded-2xl shadow-lg border border-gray-100 w-48 transform -rotate-6 animate-float" style={{ animationDelay: '0s' }}>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="bg-gray-100 p-1.5 rounded-lg"><Database className="w-4 h-4 text-gray-600" /></div>
                    <span className="text-xs font-bold text-gray-500 uppercase">PMS Data</span>
                  </div>
                  <div className="space-y-2">
                    <div className="h-1.5 bg-gray-100 rounded-full w-3/4"></div>
                    <div className="h-1.5 bg-gray-100 rounded-full w-full"></div>
                    <div className="bg-red-50 text-red-600 text-[10px] font-bold px-2 py-1 rounded inline-block mt-1">
                      Tx: Crown #19
                    </div>
                  </div>
                  {/* Connection Line */}
                  <div className="absolute -right-12 top-1/2 w-12 h-0.5 bg-gradient-to-r from-gray-200 to-blue-200 border-t border-dashed border-gray-300"></div>
                </div>

                {/* Right Side: Patient Understanding (Conversion) */}
                <div className="absolute bottom-20 right-0 bg-white p-4 rounded-2xl shadow-lg border border-gray-100 w-48 transform rotate-3 animate-float" style={{ animationDelay: '1.5s' }}>
                   <div className="flex items-center gap-3 mb-2">
                    <div className="bg-green-100 p-1.5 rounded-lg"><CheckCircle className="w-4 h-4 text-green-600" /></div>
                    <span className="text-xs font-bold text-green-600 uppercase">Accepted</span>
                  </div>
                  <p className="text-xs text-gray-600 leading-snug">
                    "I understand why I need this now. Let's schedule it."
                  </p>
                  {/* Connection Line */}
                  <div className="absolute -left-12 top-1/2 w-12 h-0.5 bg-gradient-to-l from-green-100 to-blue-200 border-t border-dashed border-green-200"></div>
                </div>

                {/* Top: Narrative Generation */}
                <div className="absolute top-0 right-10 bg-blue-600 text-white p-3 rounded-xl shadow-lg transform translate-y-4 z-30">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-blue-200" />
                    <span className="text-xs font-bold">Generating Narrative...</span>
                  </div>
                </div>

                {/* Bottom: ROI Badge */}
                <div className="absolute bottom-10 left-10 bg-white p-3 rounded-xl shadow-lg border border-blue-100 z-30 flex items-center gap-3">
                  <div className="bg-green-100 p-2 rounded-lg">
                    <DollarSign className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 font-medium">Potential Revenue</div>
                    <div className="text-sm font-bold text-gray-900">$1,200 Recovered</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Section - Text Left, Audio Right */}
      <section className="py-20 px-6 bg-white overflow-hidden border-b border-gray-50">
        <div className="container mx-auto max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left: Content */}
            <div className="space-y-8 relative z-10">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 text-blue-600 text-xs font-medium border border-blue-100 mb-4 uppercase tracking-wide">
                  Educate & Convert
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 leading-tight">
                  She doesn't sell. <br/>
                  <span className="text-blue-600">She educates responsibly.</span>
                </h2>
                <p className="text-lg text-gray-600 leading-relaxed">
                  Patients often delay treatment because they don't understand the urgency. Kim analyzes the clinical notes for each unscheduled treatment plan and creates a customized explanation of the risks of waiting.
                </p>
                <p className="text-lg text-gray-600 leading-relaxed mt-4">
                  This isn't a sales pitch. It's a compassionate, clinical conversation that helps patients make the right decision for their health—which happens to be the best decision for your practice's growth.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 pt-2">
                <Link to="/book-demo">
                  <button className="px-8 py-4 rounded-xl font-semibold text-white bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-200 hover:shadow-blue-300 transition-all transform hover:-translate-y-0.5">
                    Book a Demo
                  </button>
                </Link>
                <button className="px-8 py-4 rounded-xl font-semibold text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-all">
                  Hear Samples
                </button>
              </div>
            </div>

            {/* Right: Audio Demo Player */}
            <div className="relative">
              {/* Background Decorative Elements */}
              <div className="absolute -top-10 -right-10 w-64 h-64 bg-blue-100 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob"></div>
              <div className="absolute -bottom-10 -left-10 w-64 h-64 bg-indigo-100 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob animation-delay-2000"></div>
              
              <div className="relative bg-white rounded-3xl shadow-2xl border border-gray-100 overflow-hidden">
                <div className="bg-[#0f172a] p-6 text-white border-b border-gray-800">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-xl font-bold">Watch Kim In Action</h3>
                      <p className="text-gray-400 text-sm">See how she educates and converts</p>
                    </div>
                    <div className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center backdrop-blur-sm">
                      <MonitorPlay className="w-5 h-5 text-blue-400" />
                    </div>
                  </div>
                </div>
                
                <div className="aspect-video w-full bg-slate-900 relative group">
                    <iframe 
                        src="https://www.loom.com/embed/840a2fbe89a24a3d81700f981b4d0b78" 
                        frameBorder="0" 
                        webkitallowfullscreen="true" 
                        mozallowfullscreen="true" 
                        allowFullScreen 
                        className="absolute top-0 left-0 w-full h-full"
                    ></iframe>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* THREE KEY PILLARS SECTION - Completely Redesigned Visuals */}
      <section className="py-24 px-6 bg-gradient-to-b from-white to-blue-50/30">
        <div className="container mx-auto max-w-7xl">
            <div className="text-center mb-16 max-w-3xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 leading-tight">
                    How Kim Drives <br/>
                    <span className="text-blue-600">Incremental Revenue</span>
                </h2>
                <p className="text-lg text-gray-600 leading-relaxed">
                    A complete system designed to identify opportunity, educate responsibly, and close the loop.
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                {/* Card 1: Deep PMS Integration (Redesigned) */}
                <div className="flex flex-col">
                    <div className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-[2.5rem] p-8 h-[400px] relative overflow-hidden mb-6 flex items-center justify-center border border-white shadow-sm group hover:shadow-md transition-all">
                         <div className="absolute inset-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:20px_20px] opacity-50"></div>
                         
                         <div className="relative w-full max-w-sm">
                            {/* Data Flow Visualization */}
                            <div className="flex flex-col gap-4">
                                {/* PMS Source */}
                                <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 flex items-center justify-between z-10">
                                    <div className="flex items-center gap-3">
                                        <div className="bg-gray-100 p-2 rounded-lg"><Database className="w-5 h-5 text-gray-600" /></div>
                                        <div>
                                            <div className="text-xs font-bold text-gray-500">PMS SOURCE</div>
                                            <div className="text-sm font-bold text-gray-900">OpenDental / Dentrix</div>
                                        </div>
                                    </div>
                                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                                </div>

                                {/* Connection Pipes */}
                                <div className="h-12 w-0.5 bg-blue-200 mx-auto relative overflow-hidden">
                                    <div className="absolute top-0 left-0 w-full h-1/2 bg-blue-500 animate-slideDown"></div>
                                </div>

                                {/* Kim's Brain / Processing */}
                                <div className="bg-white p-6 rounded-2xl shadow-lg border border-blue-100 z-20 relative">
                                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                                        ANALYZING
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center">
                                            <BrainCircuit className="w-6 h-6 text-blue-600" />
                                        </div>
                                        <div className="space-y-1">
                                            <div className="text-xs font-mono text-gray-500">Extracted Treatment Plan:</div>
                                            <div className="font-bold text-gray-900 text-sm">#14 MOD Composite</div>
                                            <div className="text-xs text-blue-600">Reason: Decay close to pulp</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                         </div>
                    </div>

                    <div className="px-2">
                        <div className="text-xs font-bold text-blue-600 uppercase tracking-wider mb-1.5 flex items-center gap-2">
                             <Database className="w-4 h-4" />
                             Step 1: Migrated Treatment Data
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2.5">She Knows What They Need</h3>
                        <p className="text-sm text-gray-600 leading-relaxed">
                            Kim works from your migrated patient data in Asha PMS. She doesn't just see "Appointment Needed," she sees the specific tooth, the procedure, and the clinical notes explaining <i>why</i> it's necessary.
                        </p>
                    </div>
                </div>

                {/* Card 2: Narrative Generation (Redesigned) */}
                <div className="flex flex-col">
                    <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-[2.5rem] p-8 h-[400px] relative overflow-hidden mb-6 flex items-center justify-center border border-white shadow-sm group hover:shadow-md transition-all">
                        <div className="absolute top-0 right-0 w-64 h-64 bg-purple-100/50 rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2"></div>
                        
                        <div className="relative w-full max-w-sm space-y-4">
                            {/* Input: Clinical Note */}
                            <div className="bg-white/60 p-3 rounded-lg border border-white/50 blur-[0.5px] scale-95 opacity-70">
                                <div className="text-[10px] text-gray-400 font-mono mb-1">RAW CLINICAL NOTE</div>
                                <div className="text-xs text-gray-500 font-mono">pt presents w/ lg decay #14... risk of pulpal involvement...</div>
                            </div>

                            {/* Transformation Arrow */}
                            <div className="flex justify-center">
                                <div className="bg-gradient-to-r from-blue-500 to-indigo-500 w-8 h-8 rounded-full flex items-center justify-center shadow-lg z-10">
                                    <Sparkles className="w-4 h-4 text-white" />
                                </div>
                            </div>

                            {/* Output: Patient Friendly Narrative */}
                            <div className="bg-white p-5 rounded-2xl shadow-xl border border-indigo-100 relative z-20">
                                <div className="flex items-start gap-3">
                                    <div className="w-8 h-8 rounded-full bg-indigo-100 flex-shrink-0 flex items-center justify-center">
                                        <MessageSquare className="w-4 h-4 text-indigo-600" />
                                    </div>
                                    <div>
                                        <div className="text-[10px] font-bold text-indigo-500 uppercase mb-1">GENERATED NARRATIVE</div>
                                        <p className="text-sm text-gray-700 leading-relaxed font-medium">
                                            "Hi Sarah, Dr. Smith noticed that the decay on your upper molar is getting close to the nerve. Treating it now is a simple filling, but if we wait, it could turn into a root canal."
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="px-2">
                        <div className="text-xs font-bold text-indigo-600 uppercase tracking-wider mb-1.5 flex items-center gap-2">
                             <BrainCircuit className="w-4 h-4" />
                             Step 2: Narrative Creation
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2.5">She Explains "Why"</h3>
                        <p className="text-sm text-gray-600 leading-relaxed">
                            Generic reminders get ignored. Kim translates complex clinical jargon into a clear, compelling narrative that helps the patient understand the urgency of their situation.
                        </p>
                    </div>
                </div>

                {/* Card 3: Omnichannel (Redesigned - Timeline View) */}
                <div className="flex flex-col lg:col-span-2 mt-8">
                    <div className="bg-white rounded-[2.5rem] p-8 h-auto min-h-[300px] relative overflow-hidden mb-6 border border-gray-200 shadow-sm flex flex-col md:flex-row items-center gap-8">
                        {/* Timeline Graphic */}
                        <div className="flex-1 w-full relative">
                            {/* Horizontal Line */}
                            <div className="absolute top-1/2 left-0 w-full h-1 bg-gray-100 -translate-y-1/2 hidden md:block"></div>
                            <div className="absolute left-6 top-0 h-full w-1 bg-gray-100 md:hidden"></div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 relative z-10">
                                {/* Touchpoint 1: SMS */}
                                <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex flex-col items-center text-center group hover:border-blue-300 transition-all">
                                    <div className="w-10 h-10 bg-blue-50 rounded-full flex items-center justify-center text-blue-600 mb-3 group-hover:scale-110 transition-transform">
                                        <MessageSquare className="w-5 h-5" />
                                    </div>
                                    <div className="text-xs font-bold text-gray-400 mb-1">Day 1 - 10:00 AM</div>
                                    <div className="font-bold text-gray-900 text-sm">Personalized SMS</div>
                                    <p className="text-xs text-gray-500 mt-1">"Dr. Smith was reviewing your chart..."</p>
                                </div>

                                {/* Touchpoint 2: Call */}
                                <div className="bg-white p-4 rounded-xl border border-blue-200 shadow-md flex flex-col items-center text-center transform md:-translate-y-4 group hover:border-blue-400 transition-all">
                                    <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center text-white mb-3 shadow-lg shadow-blue-200 group-hover:scale-110 transition-transform">
                                        <Phone className="w-6 h-6" />
                                    </div>
                                    <div className="text-xs font-bold text-blue-600 mb-1">Day 2 - 4:30 PM</div>
                                    <div className="font-bold text-gray-900 text-sm">AI Voice Call</div>
                                    <p className="text-xs text-gray-500 mt-1">Educates & Handles Objections</p>
                                </div>

                                {/* Touchpoint 3: Conversion */}
                                <div className="bg-white p-4 rounded-xl border border-green-200 shadow-sm flex flex-col items-center text-center group hover:border-green-300 transition-all">
                                    <div className="w-10 h-10 bg-green-50 rounded-full flex items-center justify-center text-green-600 mb-3 group-hover:scale-110 transition-transform">
                                        <Calendar className="w-5 h-5" />
                                    </div>
                                    <div className="text-xs font-bold text-green-600 mb-1">Day 2 - 4:35 PM</div>
                                    <div className="font-bold text-gray-900 text-sm">Appointment Booked</div>
                                    <p className="text-xs text-gray-500 mt-1"> saved in Asha PMS immediately</p>
                                </div>
                            </div>
                        </div>

                        {/* Text Side */}
                        <div className="md:w-1/3 flex-shrink-0">
                             <div className="text-xs font-bold text-blue-600 uppercase tracking-wider mb-1.5 flex items-center gap-2">
                                 <LayoutGrid className="w-4 h-4" />
                                 Step 3: Omnichannel Execution
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-2.5">Intelligent Campaigns</h3>
                            <p className="text-sm text-gray-600 leading-relaxed mb-4">
                                Kim leverages both SMS and Voice to reach patients on their preferred channels. She is persistent but polite, ensuring no opportunity slips through the cracks.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
      </section>

      {/* ROI SECTION - High Ticket Focus */}
      <section className="py-24 px-6 bg-white border-t border-gray-50">
        <div className="container mx-auto max-w-7xl">
          <div className="bg-[#0f172a] rounded-[3rem] p-10 md:p-16 text-white relative overflow-hidden">
            {/* Background Glows */}
            <div className="absolute top-0 right-0 w-96 h-96 bg-blue-600/20 rounded-full blur-[100px] pointer-events-none"></div>
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-indigo-600/20 rounded-full blur-[100px] pointer-events-none"></div>

            <div className="grid md:grid-cols-2 gap-12 items-center relative z-10">
                <div>
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-green-500/10 text-green-400 text-xs font-bold border border-green-500/20 mb-6">
                        <DollarSign className="w-3 h-3" />
                        HIGH TICKET VALUE
                    </div>
                    <h2 className="text-3xl md:text-5xl font-bold mb-6 leading-tight">
                        Small Conversions.<br/>
                        <span className="text-blue-400">Massive Impact.</span>
                    </h2>
                    <p className="text-slate-300 text-lg leading-relaxed mb-8">
                        You don't need to convert hundreds of patients to see a return. <strong>Rowland Family Dentistry</strong> saw a <strong>$59,850 production increase</strong> by converting high-value treatment plans that were sitting unscheduled in their PMS.
                    </p>
                    
                    <div className="flex flex-col gap-4">
                        <div className="flex items-center gap-4 bg-white/5 p-4 rounded-xl border border-white/10">
                            <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center text-xl font-bold">54</div>
                            <div>
                                <div className="font-bold text-white">New Patients</div>
                                <div className="text-sm text-slate-400">Booked automatically</div>
                            </div>
                        </div>
                        <div className="flex items-center gap-4 bg-white/5 p-4 rounded-xl border border-white/10">
                            <div className="w-12 h-12 bg-indigo-600 rounded-lg flex items-center justify-center text-xl font-bold">12x</div>
                            <div>
                                <div className="font-bold text-white">ROI</div>
                                <div className="text-sm text-slate-400">Immediate return on investment</div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ROI Visual */}
                <div className="relative h-[400px] bg-white/5 rounded-3xl border border-white/10 p-8 flex flex-col justify-end overflow-hidden group">
                     {/* Bars */}
                     <div className="flex items-end justify-center gap-6 h-full pb-8">
                         <div className="w-16 bg-white/10 rounded-t-lg h-1/3 relative group-hover:opacity-50 transition-opacity">
                             <div className="absolute -top-8 w-full text-center text-slate-400 text-sm">Without Kim</div>
                         </div>
                         <div className="w-16 bg-gradient-to-t from-blue-600 to-indigo-400 rounded-t-lg h-3/4 relative shadow-[0_0_30px_rgba(59,130,246,0.5)]">
                             <div className="absolute -top-10 w-full text-center font-bold text-white text-lg">$59k+</div>
                             <div className="absolute top-4 left-1/2 -translate-x-1/2 w-full text-center text-white/80 text-xs font-medium">With Kim</div>
                         </div>
                     </div>
                     <div className="text-center text-slate-400 text-sm border-t border-white/10 pt-4">
                        Production Increase (Rowland Family Dentistry)
                     </div>
                </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6">
        <div className="container mx-auto max-w-4xl text-center">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Ready to fill your schedule?</h2>
            <p className="text-xl text-gray-600 mb-10 max-w-2xl mx-auto">
                See exactly how Kim works after migration to Asha PMS and how she would sound representing your practice.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/book-demo">
                    <button className="px-10 py-5 rounded-full font-bold text-lg text-white bg-blue-600 hover:bg-blue-700 shadow-xl shadow-blue-200 hover:shadow-blue-300 transition-all transform hover:-translate-y-1">
                        Book a Personalized Demo
                    </button>
                </Link>
            </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default KimPage;






























